import React, { useEffect, useState } from 'react';
import { Box, Tab, Tabs, Typography } from '@mui/material';
import { Timeline, TimelineItem, TimelineSeparator, TimelineConnector, TimelineContent, TimelineDot, timelineItemClasses } from "@mui/lab";
import Interaction from '../../interfaces/interaction';
import FieldItem from '../baseComponents/FieldItem';
import { checkIfAudioBasedDisposition, checkIfFileBasedDisposition, extractInteractionLevel, generateCurrentDate } from '../../utils/commonUtils';
import { InteractionAudit } from '../../interfaces/interactionAudit';
import InteractionValue from '../../interfaces/interactionValue';
import apiClient from '../../axiosClient';
import Audit from '../../interfaces/audit';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';
import DeliveryDiningIcon from '@mui/icons-material/DeliveryDining';
import EditNoteIcon from '@mui/icons-material/EditNote';
import CollapsibleComponent from './collapsibleComponents';
import { DISPOSITION_INPUT_TYPES, LINK } from '../../constants/constants';
import CallLog from '../../interfaces/callLogs';
import LazyAudioPlayer from './lazyAudioPlayer';

interface InteractionHistoryDetailsProps {
    row?: InteractionAudit;
    client_name: string;
    client_reference_id: string;
    refresh?: boolean;
    case_id: string; 
}

const styles = {
    heading: {
        fontSize: '16px',
        fontWeight: 'bold !important',
        wordWrap: 'break-word',
        overflowWrap: 'break-word',
        color: '#6e6e6e',
        width: '100%',
    },
    value: {
        fontSize: '16px',
        fontWeight: 'bold',
        wordWrap: 'break-word',
        overflowWrap: 'break-word',
        color: '#708090',
    },
};

const InteractionHistoryDetails: React.FC<InteractionHistoryDetailsProps> = ({ refresh, row, client_name, client_reference_id, case_id }) => {
    const currentDate = generateCurrentDate();
    const [latestInteractionId, setLatestInteractionId] = useState<string>("");
    const [combinedHistory, setCombinedHistory] = useState<(Interaction | Audit | CallLog)[]>([]);
    const [interactionData, setInteractionData] = useState<Interaction[]>([]);
    const [auditData, setAuditData] = useState<Audit[]>([]);
    const [groupedHistory, setGroupedHistory] = useState<Record<string, (Interaction | Audit | CallLog)[]>>();
    const [bestInteraction, setBestInteraction] = useState<Interaction>();
	const [callLogs, setCallLogs] = useState<CallLog[]>([]);

    const isAudit = (item: Interaction | Audit | CallLog): item is Audit => "audit_id" in item;
    const isInteraction = (item: Interaction | Audit | CallLog): item is Interaction => "interaction_id" in item;
	const isCallLog = (item: Interaction | Audit | CallLog): item is CallLog => "call_log_id" in item;

    const fetchInteractionHistoryData = async () => {
        try {
            const response = await apiClient.get('/api/v1/interaction_history', {
                params: {
                  client_reference_id,
                  client_name
                }
              });
            if (response.status === 200) {
                const initialInteractionHistoryData = response.data.map((val: { [key: string]: any }) => {
                    return {
                        ...val.interaction_history,
                        translated_text: val.translated_text
                    };
                });
                let interactionHistoryData = Interaction.initialiseInteraction(initialInteractionHistoryData);
                if (Array.isArray(interactionHistoryData)) {
                    interactionHistoryData = interactionHistoryData.sort(
                        (a, b) => new Date(b.created_date).getTime() - new Date(a.created_date).getTime()
                    );
                    setLatestInteractionId(interactionHistoryData[0]?.interaction_id || "");
                    setInteractionData(interactionHistoryData);
                }
            }
        } catch (error) {
            alert("Error while loading additional data list: fetching Interaction data: " + error);
        }
    }

    const fetchAuditData = async () => {
        try {
            const response = await apiClient.get('/api/v1/audit?client_reference_id=' + client_reference_id + "&field_ids=12380bea-c3a6-4a16-9d79-1ef99e7abf70,94014c03-f373-471d-bd5d-ed79c8f423ec,f7c9733c-754d-41e1-9f06-5abeb8ff765b");  // field_ids for status, assigned_to,collected_amount respectively
            if (response.status === 200) {
                let auditData = Audit.initialiseAudit(response.data);
                setAuditData(auditData);
            }
        } catch (error) {
            alert("Error while loading additional data list: fetching Audit data" + error);
        }
    }

    const fetchBestInteraction = async () => {
        try {
            const response = await apiClient.get('/api/v1/case/' + case_id);
            if (response.status === 200) {
                setBestInteraction(response.data.interaction_best);
            }
        } catch (error) {
            alert("Error while loading additional data list: fetching Case data" + error);
        }
    };

	const fetchCallLogs = async () => {
		try {
			const response = await apiClient.get('/api/v1/call_logs', {
				params: {
					client_name,
					client_reference_id
				}
			});
			if (response.status === 200) {
				setCallLogs(response.data);
			}	
		} catch (error) {
			alert("Error while loading call logs: " + error);
		}
	}

    useEffect(() => {
        fetchInteractionHistoryData();
        fetchAuditData();
        fetchBestInteraction();
		fetchCallLogs();
    }, [row, refresh, case_id]);

    useEffect(() => {
        const mergedData: (Interaction | Audit | CallLog)[] = [...interactionData, ...auditData, ...callLogs];

        // Sort by `created_date` (assuming it's a valid timestamp string)
        mergedData.sort((a, b) => new Date(b.created_date).getTime() - new Date(a.created_date).getTime());
        setCombinedHistory(mergedData);
    },[interactionData, auditData, callLogs])
    
    useEffect(() => {

        const val = combinedHistory.reduce((acc, item) => {
            const dateKey = item.created_date.slice(0, 10); // Extract YYYY-MM-DD
            if (!acc[dateKey]) {
                acc[dateKey] = [];
            }

            acc[dateKey].push(item);
            return acc;
        }, {} as Record<string, (Interaction | Audit | CallLog)[]>)

        setGroupedHistory(val);
    }, [combinedHistory])

    const InteractionDetails: React.FC<{ interactionDetails: InteractionValue[], translatedText?: string }> = ({ interactionDetails, translatedText = "" }) => {
        return (
            <Box sx={{ mt: 1 }}>
                {interactionDetails.map((item: InteractionValue, index: number) => {
                    if (checkIfFileBasedDisposition(item.field_value)) {
                        return (
                            <Box key={index}>
                                <FieldItem
                                    fieldName={item.field_name}
                                    value={JSON.parse(item.field_value)?.value}
                                    fieldType={DISPOSITION_INPUT_TYPES.file}
                                />
                            </Box>
                        );
                    } else if (checkIfAudioBasedDisposition(item.field_value)) {
                        return (
                            <Box key={index}>
                                <Typography sx={[styles.heading, { mb: 1 }]}>{item.field_name} Audio Recording</Typography>
                                <LazyAudioPlayer src={JSON.parse(item.field_value)?.value} />
                                {translatedText ? (
                                    <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5, padding: 0.5, background: "#FFEBE5" }}>
                                        <Typography sx={styles.heading}>{item.field_name} AI Summary</Typography>
                                        <Typography sx={styles.value}>{translatedText}</Typography>
                                    </Box>
                                ) : null}
                            </Box>
                        );
                    } else {
                        return (
                            <Box key={index}>
                                <FieldItem
                                    fieldName={item.field_name}
                                    value={item.field_value}
                                />
                            </Box>
                        );
                    }
                })}
            </Box>
        );
    };


    return (
        <div>
        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
            <Box sx={{ p: 1, border: '1px solid #cccccc', mt: 2, borderRadius: 1, minWidth: '23vw' }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="body1" fontWeight="bold" gutterBottom color="blue">
                        Best Interaction
                    </Typography>
                    <Typography variant="body1" fontWeight="bold" gutterBottom color="blue">
                        {bestInteraction?.created_date?.slice(0, 10)}
                    </Typography>
                </Box>
                <FieldItem
                    fieldName="L0 Interaction"
                    value={extractInteractionLevel(bestInteraction?.disposition_order, 0, 1)}
                    customFontSize="12px"
                />
                <FieldItem
                    fieldName="L1 Interaction"
                    value={extractInteractionLevel(bestInteraction?.disposition_order, 1, 2)}
                    customFontSize="12px"
                />
                {bestInteraction?.ptp_date && (
                    <FieldItem
                        fieldName="PTP Date"
                        value={bestInteraction.ptp_date}
                        customFontSize="12px"
                    />
                )}
                {bestInteraction?.followup_date && (
                    <FieldItem
                        fieldName="Follow Up Date"
                        value={bestInteraction.followup_date}
                        customFontSize="12px"
                    />
                )}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
                    <Box>
                        <Typography variant="subtitle2" fontWeight="bold" gutterBottom color="blue">
                            Month's Interactions
                        </Typography>
                        <Typography sx={[styles.value, { textAlign: 'center' }]}>
                            {interactionData.filter(val => val.created_date.slice(0, 7) === currentDate.slice(0, 7)).length}
                        </Typography>
                    </Box>
                    <Box>
                        <Typography textAlign="right" variant="subtitle2" fontWeight="bold" gutterBottom color="blue">
                            Today's Interactions
                        </Typography>
                        <Typography sx={[styles.value, { textAlign: 'center' }]}>
                            {interactionData.filter(val => val.created_date.slice(0, 10) === currentDate).length}
                        </Typography>
                    </Box>
                </Box>
            </Box>
        </Box>
        <Tabs value={0} sx={{ marginTop: "4px" }}>
			<Tab label="History" style={{ minWidth: "100%", fontWeight: "bolder", fontSize: "18px" }} />
        </Tabs>
        <Box sx={{ maxHeight: "86vh", overflowY: "auto", maxWidth:"32vw" }}>
            {groupedHistory &&
                Object.entries(groupedHistory)
                    .sort(([dateA, itemsA], [dateB, itemsB]) => {
                        const dateATime = new Date(dateA).getTime();
                        const dateBTime = new Date(dateB).getTime();
                        if(row){
                            // Check if itemsA or itemsB contain the matching interaction
                            const hasMatchingA = itemsA.some(
                                (item) => "interaction_id" in item && item.interaction_id === row.interaction_id
                            );
                            const hasMatchingB = itemsB.some(
                                (item) => "interaction_id" in item && item.interaction_id === row.interaction_id
                            );

                            // Move the group with the matching interaction to the top
                            if (hasMatchingA && !hasMatchingB) return -1;
                            if (hasMatchingB && !hasMatchingA) return 1;
                        }
                        // Otherwise, sort by date (descending)
                        return dateBTime - dateATime;
                    })
                    .map(([date, items]) => (
                        <Box key={date} sx={{ mb: 4}}>
                            {/* Date Header */}
                            <Typography variant="h6" fontWeight="bold" gutterBottom sx={{ color: "#1976D2" , paddingLeft:2}}>
                                {date}
                            </Typography>

                            <Timeline
                                sx={{
                                    [`& .${timelineItemClasses.root}:before`]: {
                                        flex: 0,
                                        padding: 0,
                                    },
                                }}
                                >
                                {items
                                    .sort((a, b) => {
                                        const dateATime = new Date(a.created_date).getTime();
                                        const dateBTime = new Date(b.created_date).getTime();
                                        if(row){
                                            const isMatchingA = "interaction_id" in a && a.interaction_id === row.interaction_id;
                                            const isMatchingB = "interaction_id" in b && b.interaction_id === row.interaction_id;

                                            // Move the matching interaction to the top
                                            if (isMatchingA && !isMatchingB) return -1;
                                            if (isMatchingB && !isMatchingA) return 1;
                                        }
                                        // Otherwise, sort by date (descending)
                                        return dateBTime - dateATime;
                                    })
                                    .map((item, index) => (
                                        <TimelineItem key={index} sx={{padding:0}}>

                                            <TimelineSeparator>
                                            {
                                                isAudit(item) ?
                                                <TimelineDot sx={{ border: "2px solid error", backgroundColor: "error" }} color={isAudit(item) ? "error" : "primary"} >
                                                <EditNoteIcon fontSize='large' />
                                            </TimelineDot>                             
                                            :
                                            <TimelineDot sx={{ border: "2px solid #1976D2", backgroundColor: "#1976D2" }} >
                                            {(isInteraction(item) && (item.interaction_mode === "CALL" || item.interaction_mode === "Call" )) || isCallLog(item) ? (
                                                <SupportAgentIcon fontSize='large' />
                                            ) : (
                                                <DeliveryDiningIcon fontSize='large' />
                                            )}
                                            </TimelineDot>
                                            }
                                            {index !== items.length - 1 && <TimelineConnector />}
                                            </TimelineSeparator>
                                            <TimelineContent>
                                                {/* Display Time HH:mm */}
                                                <Typography variant="subtitle2" fontWeight="bold">
                                                {item.created_date.slice(11, 16)}{" "}
                                                <Typography component="span" variant="body2" sx={{ color: "gray", fontWeight: "normal" }}>
                                                    by:
                                                </Typography>{" "}
                                                {item.created_by}
                                                </Typography>

                                                {isAudit(item) ? (
                                                    <Box sx={{ p: 1, border: "1px dashed red", bgcolor: "#FFE6E6", maxWidth: "23vw" }}>
                                                        <Typography variant="body1">
                                                            <strong>Audit of {item.field_name} :</strong> {" "}{item.old_field_value} → {item.new_field_value} 
                                                        </Typography>
                                                    </Box>
                                                ) : isInteraction(item) ? (
                                                    <Box sx={{ p: 1, border: "1px dashed grey", bgcolor: "#E6F7FF", maxWidth: "23vw" }}>
                                                        {item.interaction_id === latestInteractionId && (
                                                            <Typography variant="body1" fontWeight="bold" gutterBottom color="blue">
                                                                Latest Interaction
                                                            </Typography>
                                                        )}

                                                        {/* Highlight the interaction being audited */}
                                                        {row && item.interaction_id === row.interaction_id && (
                                                            <Box display="flex" justifyContent="space-between" alignItems="center">
                                                                <Typography variant="h6" fontWeight="bold" gutterBottom color="red">
                                                                    Interaction to Audit
                                                                </Typography>
                                                                <Box bgcolor="#FFA500" color="white" px={1}>
                                                                    <Typography variant="h6" fontWeight="bold" sx={{ m: 0 }}>
                                                                        {extractInteractionLevel(item.disposition_order, 0, 1)}
                                                                    </Typography>
                                                                </Box>
                                                            </Box>
                                                        )}

                                                        <Box>
                                                            <FieldItem fieldName={"Interaction Mode"} value={item.interaction_mode} />
                                                        </Box>
                                                        <Box>
                                                            <FieldItem fieldName={"Interaction Type"} value={item.interaction_type} />
                                                        </Box>
                                                        <Box>
                                                            <FieldItem fieldName={"Agent"} value={item.created_by} />
                                                        </Box>
                                                        <Box>
                                                            <FieldItem fieldName={"Visited Address"} value={item.visited_address ?? "N/A"} />
                                                        </Box>
                                                         <Box>
                                                             <FieldItem fieldName={"Latitude"} value={item.latitude && item.latitude.trim() !== "" ? item.latitude : "N/A"} />
                                                         </Box>
                                                         <Box>
                                                             <FieldItem fieldName={"Longitude"} value={item.longitude && item.longitude.trim() !== "" ? item.longitude : "N/A"} />
                                                         </Box>
                                                        <Box>
                                                            <FieldItem
                                                                fieldName={"L0 Interaction"}
                                                                value={extractInteractionLevel(item.disposition_order, 0, 1)}
                                                                />
                                                            <FieldItem
                                                                fieldName={"L1 Interaction"}
                                                                value={extractInteractionLevel(item.disposition_order, 1, 2)}
                                                                />
                                                        </Box>
                                                        <Box>
                                                            <FieldItem
                                                                fieldName={"Sub Disposition"}
                                                                value={extractInteractionLevel(item.disposition_order, 2)}
                                                                />
                                                        </Box>
                                                        <Box>
                                                            <FieldItem fieldName={"Ptp Date"} value={item.ptp_date ?? "N/A"} />
                                                        </Box>
                                                        <Box>
                                                            <FieldItem fieldName={"Followup Date"} value={item.followup_date ?? "N/A"} />
                                                        </Box>
                                                        <InteractionDetails interactionDetails={item.disposition_value} />
                                                        <Box>
                                                            {item.image_url !== "" && (
                                                                <FieldItem fieldName={"Image_urls"} fieldType={LINK} value={item.image_url} />
                                                            )}
                                                        </Box>
                                                        <Box>
                                                            <FieldItem
                                                                fieldName={"Interaction Capture Time"}
                                                                value={`${item.created_date.slice(0, 10)} / Time: ${item.created_date.slice(11, 16)}`}
                                                                />
                                                        </Box>
                                                        <CollapsibleComponent
                                                            type="basic"
                                                            headerText="Comments"
                                                            content={<Typography>{item.comment}</Typography>}
                                                        />
                                                    </Box>
                                                ) : isCallLog(item) ? (
                                                    <>
                                                        {item.call_result === "Answered" ? (
                                                            <Box key={index} sx={{ gap: 0.5 }}>
                                                                <FieldItem fieldName="Call Type" value={item.call_type} />
                                                                <FieldItem fieldName="Start Time of Call" value={item.start_time_of_call} />
                                                                <FieldItem fieldName="Duration" value={item.duration} />
                                                                <FieldItem fieldName="Call Result" value={item.call_result} />
                                                                <FieldItem fieldName="Dialled Number" value={item.callee_did} />
                                                                <FieldItem fieldName="Agent Name" value={item.caller} />
                                                                <FieldItem fieldName="Agency Name" value={item.agency_name} />
                                                                {item.duration > 0 && (
                                                                    <LazyAudioPlayer src={item.call_recording_storage_key} />
                                                                )}
                                                            </Box>
                                                        ) : (
                                                            <Box key={index} sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
                                                                <Typography sx={{ color: '#708090', fontWeight: 'bold' }} variant="subtitle2">
                                                                    {item.call_type}
                                                                </Typography>
                                                                <Typography sx={{ color: '#708090', fontWeight: 'bold' }} variant="subtitle2">
                                                                    {item.call_result}
                                                                </Typography>
                                                                <Typography sx={{ color: '#708090', fontWeight: 'bold' }} variant="subtitle2">
                                                                    {item.callee_did}
                                                                </Typography>
                                                            </Box>
                                                        )}
                                                    </>
												) : null}
                                            </TimelineContent>
                                        </TimelineItem>
                                    ))}
                            </Timeline>
                        </Box>
                    ))}
        </Box>
        </div>
    );
};

export default InteractionHistoryDetails;
